import React from 'react';
import { Form, Input, Button, Spin, Select, Upload, Card, message, Radio, Alert, Table, Divider } from 'antd';
import axios from 'axios'
import * as serverconfig from '../../serverconn'
import { UploadOutlined, InboxOutlined, LoadingOutlined, DownloadOutlined, FileTextOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';

var CryptoJS = require("crypto-js");
const { Option } = Select;
const { Dragger } = Upload;

const FormItem = Form.Item;
const antIcon = <LoadingOutlined style={{ fontSize: 60 }} spin />;

var token = '';

class DataImport extends React.Component {

  state = {
    datarequested: false,
    importType: 'hubs', // Default to hubs import
    selectedFile: null,
    excelData: [],
    previewData: [],
    showPreview: false,
    importResults: null,
    // Manual barcode entry
    manualBarcodeEntry: '',
    manualBarcodeType: 'single_entry',
    // PDF processing
    pdfProcessing: false,
    uploadMethod: 'excel', // 'excel' or 'pdf'
  }

  componentDidMount() {
    if (localStorage.getItem("token")) {
      token = localStorage.getItem("token");
    } else {
      token = '';
    }

    axios.defaults.headers = {
      "Content-Type": "application/json",
      Authorization: `Token ${token}`
    }
  }

  // Handle import type change
  handleImportTypeChange = (e) => {
    this.setState({
      importType: e.target.value,
      selectedFile: null,
      excelData: [],
      previewData: [],
      showPreview: false,
      importResults: null,
    });
  }

  // Custom upload handler
  handleFileUpload = (file) => {
    this.setState({ selectedFile: file });
    
    if (this.state.importType === 'barcodes') {
      // For barcodes, check file type
      if (file.type === 'application/pdf') {
        this.setState({ uploadMethod: 'pdf' });
        this.processPDFFile(file);
      } else if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
        this.setState({ uploadMethod: 'excel' });
        this.parseExcelFile(file);
      } else {
        message.error('Please upload either an Excel file (.xlsx/.xls) or PDF file (.pdf)');
        return false;
      }
    } else {
      this.parseExcelFile(file);
    }
    return false; // Prevent auto upload
  }

  // Parse Excel file and convert to JSON
  parseExcelFile = (file) => {
    const reader = new FileReader();
    const self = this; // Store reference for Node.js 14 compatibility
    
    reader.onload = function(e) {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        
        // Get the first worksheet
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        
        // Convert to JSON
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        
        if (self.state.importType === 'hubs') {
          self.processHubsData(jsonData);
        } else if (self.state.importType === 'healthcenters') {
          self.processHealthCentersData(jsonData);
        } else if (self.state.importType === 'barcodes') {
          self.processBarcodesData(jsonData);
        }
        
      } catch (error) {
        console.error('Error parsing Excel file:', error);
        message.error('Error parsing Excel file. Please check the file format.');
      }
    };
    reader.readAsArrayBuffer(file);
  }

  // Process hubs data specifically
  processHubsData = (rawData) => {
    if (rawData.length < 2) {
      message.error('Excel file must contain at least a header row and one data row');
      return;
    }

    // Skip header row and process data
    const processedData = [];
    for (let i = 1; i < rawData.length; i++) {
      const row = rawData[i];
      if (row.length >= 4 && row[1] && row[2] && row[3]) { // Ensure we have all required fields
        processedData.push({
          no: row[0] || i,
          hub: row[1],
          district: row[2],
          region: row[3]
        });
      }
    }

    this.setState({
      excelData: processedData,
      previewData: processedData.slice(0, 10), // Show first 10 rows for preview
      showPreview: true
    });

    message.success(`Parsed ${processedData.length} hub records from Excel file`);
  }

  // Process health centers data specifically
  processHealthCentersData = (rawData) => {
    if (rawData.length < 2) {
      message.error('Excel file must contain at least a header row and one data row');
      return;
    }

    // Skip header row and process data
    const processedData = [];
    for (let i = 1; i < rawData.length; i++) {
      const row = rawData[i];
      if (row.length >= 6 && row[0] && row[1]) { // Ensure we have at least name and hub
        processedData.push({
          healthcenter: row[0] || '',
          hub: row[1] || '',
          contactpersonname: row[2] || '',
          contactpersonphone: row[3] || '',
          location_latitude: row[4] || 0.0,
          location_longitude: row[5] || 0.0
        });
      }
    }

    this.setState({
      excelData: processedData,
      previewData: processedData.slice(0, 10), // Show first 10 rows for preview
      showPreview: true
    });

    message.success(`Parsed ${processedData.length} health center records from Excel file`);
  }

  // Process barcodes data specifically
  processBarcodesData = (rawData) => {
    if (rawData.length < 2) {
      message.error('Excel file must contain at least a header row and one data row');
      return;
    }

    // Skip header row and process data
    const processedData = [];
    for (let i = 1; i < rawData.length; i++) {
      const row = rawData[i];
      if (row.length >= 2 && row[0] && row[1]) { // Ensure we have barcode and type
        const barcodeType = row[1].toString().toLowerCase();
        if (barcodeType === 'single_entry' || barcodeType === 'batch_entry' || barcodeType === 'package') {
          processedData.push({
            barcode: row[0].toString().trim(),
            type: barcodeType
          });
        } else {
          console.warn(`Invalid barcode type at row ${i + 1}: ${row[1]}. Expected 'single_entry', 'batch_entry' or 'package'`);
        }
      }
    }

    this.setState({
      excelData: processedData,
      previewData: processedData.slice(0, 10), // Show first 10 rows for preview
      showPreview: true
    });

    message.success(`Parsed ${processedData.length} barcode records from Excel file`);
  }

  // Process PDF file to extract barcodes
  processPDFFile = (file) => {
    this.setState({ pdfProcessing: true });
    
    const formData = new FormData();
    formData.append('pdf_file', file);
    formData.append('barcode_type', this.state.manualBarcodeType || 'single_entry');

    const self = this;

    axios.post(
      `${serverconfig.backendserverurl}/customqueries/extract_pdf_barcodes`,
      formData,
      {
        headers: {
          'content-type': 'multipart/form-data',
          Authorization: `Token ${token}`
        }
      }
    )
    .then(function(response) {
      if (response.data.success) {
        const extractedBarcodes = response.data.barcodes.map(barcode => ({
          barcode: barcode,
          type: self.state.manualBarcodeType || 'single_entry'
        }));

        self.setState({
          excelData: extractedBarcodes,
          previewData: extractedBarcodes.slice(0, 10),
          showPreview: true,
          pdfProcessing: false
        });

        message.success(`Extracted ${extractedBarcodes.length} barcodes from PDF file`);
      } else {
        throw new Error(response.data.message || 'Failed to extract barcodes from PDF');
      }
    })
    .catch(function(error) {
      console.error('PDF processing error:', error);
      self.setState({ pdfProcessing: false });
      message.error(error.response?.data?.message || 'Failed to process PDF file. Please try again.');
    });
  }

  // Submit data to backend
  handleFormSubmit = () => {
    if (!this.state.selectedFile || this.state.excelData.length === 0) {
      const fileType = this.state.importType === 'barcodes' && this.state.uploadMethod === 'pdf' ? 'PDF' : 'Excel';
      message.error(`Please select and parse a ${fileType} file first`);
      return;
    }

    this.setState({ datarequested: true });

    const formData = new FormData();
    formData.append('import_type', this.state.importType);
    formData.append('data', JSON.stringify(this.state.excelData));

    const self = this; // Store reference for callback compatibility

    axios.post(
      `${serverconfig.backendserverurl}/customqueries/import_data`,
      formData,
      {
        headers: {
          'content-type': 'multipart/form-data'
        }
      }
    )
    .then(function(response) {
      self.setState({
        datarequested: false,
        importResults: response.data
      });

      message.success(response.data.message || 'Data imported successfully!');
    })
    .catch(function(error) {
      console.error('Import error:', error);
      self.setState({ datarequested: false });
      message.error('Failed to import data. Please try again.');
    });
  }

  // Download template
  downloadTemplate = () => {
    if (this.state.importType === 'hubs') {
      this.downloadHubsTemplate();
    } else if (this.state.importType === 'healthcenters') {
      this.downloadHealthCentersTemplate();
    } else if (this.state.importType === 'barcodes') {
      this.downloadBarcodesTemplate();
    }
  }

  downloadHubsTemplate = () => {
    const templateData = [
      ['No', 'Hub', 'District', 'Region'],
      [1, 'Example Hub', 'Example District', 'Example Region'],
      [2, 'Another Hub', 'Another District', 'Another Region']
    ];

    const ws = XLSX.utils.aoa_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Hubs');
    XLSX.writeFile(wb, 'hubs_import_template.xlsx');
  }

  downloadHealthCentersTemplate = () => {
    const templateData = [
      ['HealthCenter', 'Hub', 'contactpersonname', 'contactpersonphone', 'location_latitude', 'location_longitude'],
      ['Example Health Center', 'Central Hub', 'John Doe', '+256700123456', '0.3136', '32.5811'],
      ['Another Health Center', 'Northern Hub', 'Jane Smith', '+256700654321', '2.2048', '32.8973']
    ];

    const ws = XLSX.utils.aoa_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'HealthCenters');
    XLSX.writeFile(wb, 'healthcenters_import_template.xlsx');
  }

  downloadBarcodesTemplate = () => {
    const templateData = [
      ['Barcode', 'Type'],
      ['BC001234567890', 'single_entry'],
      ['BC001234567891', 'batch_entry'],
      ['BC001234567892', 'single_entry'],
      ['900000000001', 'package']
    ];

    const ws = XLSX.utils.aoa_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Barcodes');
    XLSX.writeFile(wb, 'barcodes_import_template.xlsx');
  }

  // Get column configuration based on import type
  getPreviewColumns = () => {
    if (this.state.importType === 'hubs') {
      return [
        {
          title: 'No',
          dataIndex: 'no',
          key: 'no',
        },
        {
          title: 'Hub Name',
          dataIndex: 'hub',
          key: 'hub',
        },
        {
          title: 'District',
          dataIndex: 'district',
          key: 'district',
        },
        {
          title: 'Region',
          dataIndex: 'region',
          key: 'region',
        },
      ];
    } else if (this.state.importType === 'healthcenters') {
      return [
        {
          title: 'Health Center',
          dataIndex: 'healthcenter',
          key: 'healthcenter',
        },
        {
          title: 'Hub',
          dataIndex: 'hub',
          key: 'hub',
        },
        {
          title: 'Contact Person',
          dataIndex: 'contactpersonname',
          key: 'contactpersonname',
        },
        {
          title: 'Phone',
          dataIndex: 'contactpersonphone',
          key: 'contactpersonphone',
        },
        {
          title: 'Latitude',
          dataIndex: 'location_latitude',
          key: 'location_latitude',
        },
        {
          title: 'Longitude',
          dataIndex: 'location_longitude',
          key: 'location_longitude',
        },
      ];
    } else if (this.state.importType === 'barcodes') {
      return [
        {
          title: 'Barcode',
          dataIndex: 'barcode',
          key: 'barcode',
        },
        {
          title: 'Type',
          dataIndex: 'type',
          key: 'type',
        },
      ];
    }
    return [];
  }

  // Handle manual barcode submission
  handleManualBarcodeSubmit = () => {
    if (!this.state.manualBarcodeEntry.trim()) {
      message.error('Please enter a barcode value');
      return;
    }

    this.setState({ datarequested: true });

    const formData = new FormData();
    formData.append('import_type', 'barcodes');
    formData.append('is_manual_entry', 'true');
    formData.append('data', JSON.stringify([{
      barcode: this.state.manualBarcodeEntry.trim(),
      type: this.state.manualBarcodeType
    }]));

    const self = this;

    axios.post(
      `${serverconfig.backendserverurl}/customqueries/import_data`,
      formData,
      {
        headers: {
          'content-type': 'multipart/form-data'
        }
      }
    )
    .then(function(response) {
      self.setState({
        datarequested: false,
        importResults: response.data,
        manualBarcodeEntry: '', // Clear the form
      });

      message.success(response.data.message || 'Barcode created successfully!');
    })
    .catch(function(error) {
      console.error('Manual barcode creation error:', error);
      self.setState({ datarequested: false });
      message.error('Failed to create barcode. Please try again.');
    });
  }

  render() {
    if (this.state.datarequested === true) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Spin indicator={antIcon} />
        </div>
      );
    } else {
      const columns = this.getPreviewColumns();

      return (
        <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
          <Card title="Data Import" style={{ marginBottom: '20px' }}>
            
            {/* Import Type Selection */}
            <div style={{ marginBottom: '20px' }}>
              <h3>Select Import Type:</h3>
              <Radio.Group onChange={this.handleImportTypeChange} value={this.state.importType}>
                <Radio value="hubs">Collection Hubs with Regions & Districts</Radio>
                <Radio value="healthcenters">Health Centers with Hub Links</Radio>
                <Radio value="barcodes">Sample Barcodes Import</Radio>
              </Radio.Group>
            </div>

            {/* Import Type Description */}
            <div style={{ marginBottom: '20px' }}>
              <Alert
                message={this.state.importType === 'hubs' ? 
                  "Hub Import: Expected columns (No, Hub, District, Region)" :
                  this.state.importType === 'healthcenters' ?
                  "Health Center Import: Expected columns (HealthCenter, Hub, contactpersonname, contactpersonphone, location_latitude, location_longitude)" :
                   "Barcode Import: Expected columns (Barcode, Type)"
                }
                description={this.state.importType === 'hubs' ? 
                  "Creates/links regions, districts, and collection hubs." :
                  this.state.importType === 'healthcenters' ?
                  "Creates/updates health centers and links them to existing hubs using fuzzy matching. Updates contact and location information." :
                  "Creates barcodes with specified types (single_entry, batch_entry, or package). Barcodes must be unique."
                }
                type="info"
                showIcon
              />
            </div>

            {/* Template Download */}
            <div style={{ marginBottom: '20px' }}>
              <Button 
                icon={<DownloadOutlined />} 
                onClick={this.downloadTemplate}
                type="dashed"
              >
                Download Excel Template ({this.state.importType === 'hubs' ? 'Hubs' : this.state.importType === 'healthcenters' ? 'Health Centers' : 'Barcodes'})
              </Button>
            </div>

            {/* File Upload */}
            <div style={{ marginBottom: '20px' }}>
              <h3>Upload {this.state.importType === 'barcodes' ? 'Excel or PDF' : 'Excel'} File:</h3>
              
              {/* PDF/Excel Type selector for barcodes */}
              {this.state.importType === 'barcodes' && (
                <div style={{ marginBottom: '15px' }}>
                  <Radio.Group 
                    value={this.state.uploadMethod} 
                    onChange={(e) => this.setState({ uploadMethod: e.target.value, selectedFile: null, showPreview: false })}
                  >
                    <Radio value="excel">Upload Excel File</Radio>
                    <Radio value="pdf">Extract from PDF</Radio>
                  </Radio.Group>
                </div>
              )}

              <Dragger
                accept={this.state.importType === 'barcodes' ? 
                  (this.state.uploadMethod === 'pdf' ? '.pdf' : '.xlsx,.xls') : 
                  '.xlsx,.xls'
                }
                beforeUpload={this.handleFileUpload}
                showUploadList={false}
                style={{ padding: '20px' }}
              >
                <p className="ant-upload-drag-icon">
                  {this.state.importType === 'barcodes' && this.state.uploadMethod === 'pdf' ? 
                    <FileTextOutlined /> : <InboxOutlined />
                  }
                </p>
                <p className="ant-upload-text">
                  Click or drag {this.state.importType === 'barcodes' ? 
                    (this.state.uploadMethod === 'pdf' ? 'PDF' : 'Excel') : 'Excel'
                  } file to this area to upload
                </p>
                <p className="ant-upload-hint">
                  {this.state.importType === 'barcodes' ? 
                    (this.state.uploadMethod === 'pdf' ? 
                      'Support for PDF files. Barcodes will be automatically extracted using OCR technology.' :
                       'Support for .xlsx and .xls files. Expected format: Barcode, Type (single_entry, batch_entry, package)'
                    ) :
                    `Support for .xlsx and .xls files. ${this.state.importType === 'hubs' ? 
                      'Expected format: No, Hub, District, Region' :
                      'Expected format: HealthCenter, Hub, contactpersonname, contactpersonphone, location_latitude, location_longitude'
                    }`
                  }
                </p>
              </Dragger>
              
              {this.state.selectedFile && (
                <Alert
                  message={`Selected file: ${this.state.selectedFile.name}`}
                  type="info"
                  style={{ marginTop: '10px' }}
                />
              )}

              {/* Barcode Type Selector for PDF uploads */}
              {this.state.importType === 'barcodes' && this.state.uploadMethod === 'pdf' && (
                <div style={{ marginTop: '15px' }}>
                  <FormItem label="Default Barcode Type for PDF extraction:">
                    <Select
                      value={this.state.manualBarcodeType}
                      onChange={(value) => this.setState({ manualBarcodeType: value })}
                      size="large"
                      style={{ width: '200px' }}
                    >
                      <Option value="single_entry">Single Entry</Option>
                      <Option value="batch_entry">Batch Entry</Option>
                      <Option value="package">Package</Option>
                    </Select>
                  </FormItem>
                </div>
              )}

              {/* PDF Processing Status */}
              {this.state.pdfProcessing && (
                <Alert
                  message="Processing PDF file..."
                  description="Extracting barcodes using OCR technology. This may take a few moments."
                  type="info"
                  showIcon
                  style={{ marginTop: '10px' }}
                />
              )}
            </div>

            {/* Data Preview */}
            {this.state.showPreview && (
              <div style={{ marginBottom: '20px' }}>
                <h3>Data Preview (First 10 rows):</h3>
                <Table
                  columns={columns}
                  dataSource={this.state.previewData}
                  pagination={false}
                  size="small"
                  scroll={{ x: 'max-content' }}
                />
                <p style={{ marginTop: '10px', color: '#666' }}>
                  Total records to import: {this.state.excelData.length}
                </p>
              </div>
            )}

            {/* Import Button */}
            <div style={{ marginBottom: '20px' }}>
              <Button 
                type="primary" 
                size="large"
                onClick={this.handleFormSubmit}
                disabled={!this.state.showPreview || this.state.excelData.length === 0 || this.state.pdfProcessing}
                loading={this.state.datarequested || this.state.pdfProcessing}
              >
                Import {this.state.importType === 'hubs' ? 'Hub' : this.state.importType === 'healthcenters' ? 'Health Center' : 'Barcode'} Data
              </Button>
            </div>

            {/* Import Results */}
            {this.state.importResults && (
              <div>
                <Divider />
                <Alert
                  message="Import Results"
                  description={this.state.importResults.message}
                  type={this.state.importResults.success ? "success" : "error"}
                  showIcon
                />
                {this.state.importResults.details && (
                  <div style={{ marginTop: '10px' }}>
                    <pre style={{ background: '#f5f5f5', padding: '10px', borderRadius: '4px' }}>
                      {JSON.stringify(this.state.importResults.details, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            )}

          </Card>

          {/* Manual Barcode Entry Form - Only show for barcode import type */}
          {this.state.importType === 'barcodes' && (
            <Card title="Manual Barcode Entry" style={{ marginTop: '20px' }}>
              <div style={{ maxWidth: '500px' }}>
                <FormItem label="Barcode" style={{ marginBottom: '16px' }}>
                  <Input
                    placeholder="Enter barcode value"
                    value={this.state.manualBarcodeEntry}
                    onChange={(e) => this.setState({ manualBarcodeEntry: e.target.value })}
                    size="large"
                  />
                </FormItem>
                
                <FormItem label="Barcode Type" style={{ marginBottom: '16px' }}>
                  <Select
                    value={this.state.manualBarcodeType}
                    onChange={(value) => this.setState({ manualBarcodeType: value })}
                    size="large"
                    style={{ width: '100%' }}
                  >
                    <Option value="single_entry">Single Entry</Option>
                    <Option value="batch_entry">Batch Entry</Option>
                    <Option value="package">Package</Option>
                  </Select>
                </FormItem>

                <Button
                  type="primary"
                  size="large"
                  onClick={this.handleManualBarcodeSubmit}
                  disabled={!this.state.manualBarcodeEntry.trim()}
                  loading={this.state.datarequested}
                >
                  Create Single Barcode
                </Button>
              </div>
            </Card>
          )}
        </div>
      );
    }
  }
}

export default DataImport;



